/**
 * templateProject.js — turn a template into a new LiTeCAB project.
 *
 * The single builder behind every "start from a template" path: the startup
 * template picker, the copilot's create tools, and re-creating an outdated
 * project from the current template. Each project it builds is stamped with
 * `template_ref` (see templateVersion.js) so a later template fix can be noticed.
 */

import { stampTemplateRef } from './templateVersion';

// Templates may list processes as an array (older / AI-generated) or keyed by id.
function normalizeProcesses(processes) {
  if (!Array.isArray(processes)) return processes || {};
  const obj = {};
  processes.forEach((p) => {
    const id = p.process_id || p.id;
    obj[id] = {
      id,
      name: p.process_name || p.name || 'Unnamed_Process',
      process_type: p.process_type || 'Processing',
      manufacturing_category: p.manufacturing_category || 'Custom',
      hierarchy_level: p.hierarchy_level ?? 0,
      is_side_input: p.is_side_input ?? false,
      equipment: p.equipment || '',
      parameters: p.parameters || { duration_hours: p.duration_hours ?? 1, temperature_c: p.temperature_c ?? 25 },
      input_materials: p.input_materials,
      products: p.products || p.output_materials || [],
      lci_inputs: p.lci_inputs || [],
      next_process_ids: p.next_process_ids || [],
      description: p.description || '',
    };
  });
  return obj;
}

// What a project keeps of a BatPaC template's metadata: which cathode, and its
// stand-in if it has one. The configurations stay on the template.
function batpacRef(batpac) {
  return {
    chemistry_id: batpac.chemistry_id,
    label: batpac.label,
    stand_in: batpac.stand_in || null,
    configuration: batpac.configuration || 'energy',
  };
}

/**
 * @param {object} template  a loaded template (built-in, uploaded or AI-generated)
 * @param {object} opts
 * @param {string} opts.name          project name
 * @param {string} [opts.file]        built-in file name it was loaded from (stamped)
 * @param {string} [opts.chemistry]   defaults to the template's functional unit
 * @param {string} [opts.category]
 * @param {string} [opts.description] defaults to "<category> — <chemistry>"
 * @param {string} [opts.projectId]   a fixed project_id, so re-creating it replaces
 *                                    the earlier copy; normally left out and the
 *                                    store assigns a fresh one (projectIdentity.js)
 */
export function buildProjectFromTemplate(template, { name, file = null, chemistry, category, description, projectId } = {}) {
  if (!template?.processes) throw new Error('Template has no processes');
  const fu = template.functional_unit || {};
  const chem = chemistry || fu.chemistry || fu.feedstock_type || 'Custom';
  const cat = category || fu.scale || 'Custom';

  return {
    ...(projectId ? { project_id: projectId } : {}),
    project_name: name,
    template_id: template.template_id || null,
    template_ref: stampTemplateRef(template, file),
    project_info: {
      name,
      description: description || `${cat} — ${chem}`,
      chemistry: chem,
      category: cat,
      battery_modules: 1,
      scaling_factor: 1.0,
      // Per-kWh functional unit when the template carries an energy density;
      // mass basis otherwise (e.g. recycling/module templates).
      fu_basis: fu.energy_density_wh_per_kg ? 'energy' : 'mass',
      energy_density_wh_per_kg: fu.energy_density_wh_per_kg ?? null,
      created: new Date().toISOString(),
    },
    functional_unit: fu,
    // BatPaC process templates are scored on GREET; others default to ecoinvent.
    ...(template.impact_dataset ? { impact_dataset: template.impact_dataset } : {}),
    ...(template.batpac ? { batpac: batpacRef(template.batpac) } : {}),
    ...(template.design_parameters ? { design_parameters: template.design_parameters } : {}),
    // Templates authored before design_parameters; migrated on read by getDesignParameters().
    ...(template.coating_parameters ? { coating_parameters: template.coating_parameters } : {}),
    processes: normalizeProcesses(template.processes),
    analysis_results: { lca: null, tea: null },
  };
}
